import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { CheckCircle, Circle, Calendar, AlertTriangle, Upload, Lock, ChevronDown, ChevronRight, User } from 'lucide-react';
import type { Milestone } from '@/lib/types/milestone';
import { SYSTEM_MILESTONE_CONFIG } from '@/lib/types/milestone';

interface MilestoneTimelineProps {
  milestones: Milestone[];
  onMilestoneClick?: (milestone: Milestone) => void;
  showApprovalStatus?: boolean;
  onMarkComplete?: (milestone: Milestone) => void;
  onRequestChange?: (milestone: Milestone) => void;
  onEditMilestone?: (milestone: Milestone) => void;
  currentUserRole?: 'client' | 'vendor';
  canEdit?: boolean;
  canRequestChange?: boolean;
  isFullyApproved?: boolean;
  jobPublicId?: string;
  currentUserId?: string;
  onAddMilestone?: () => void;
}

export function MilestoneTimeline({ 
  milestones, 
  onMilestoneClick,
  showApprovalStatus = false,
  onMarkComplete,
  onRequestChange,
  onEditMilestone,
  currentUserRole,
  canEdit = false,
  canRequestChange = false,
  isFullyApproved = false,
  jobPublicId,
  currentUserId,
  onAddMilestone
}: MilestoneTimelineProps) {
  const navigate = useNavigate();
  const [setupAwardOpen, setSetupAwardOpen] = useState(true);
  const [completionOpen, setCompletionOpen] = useState(true);

  // Separate milestones into three groups
  const { setupAwardMilestones, userMilestones, completionMilestones } = useMemo(() => {
    const setupAward: Milestone[] = [];
    const user: Milestone[] = [];
    const completion: Milestone[] = [];

    milestones.forEach(milestone => {
      if (milestone.is_system_milestone && milestone.system_milestone_type) {
        const config = SYSTEM_MILESTONE_CONFIG[milestone.system_milestone_type];
        if (config) {
          if (config.phase === 'setup' || config.phase === 'award') {
            setupAward.push(milestone);
          } else if (config.phase === 'completion') {
            completion.push(milestone);
          }
        }
      } else if (!milestone.is_system_milestone) {
        user.push(milestone);
      }
    });

    // Sort system milestones by config order
    setupAward.sort((a, b) => {
      const aConfig = a.system_milestone_type ? SYSTEM_MILESTONE_CONFIG[a.system_milestone_type] : null;
      const bConfig = b.system_milestone_type ? SYSTEM_MILESTONE_CONFIG[b.system_milestone_type] : null;
      return (aConfig?.order || 0) - (bConfig?.order || 0);
    });

    completion.sort((a, b) => {
      const aConfig = a.system_milestone_type ? SYSTEM_MILESTONE_CONFIG[a.system_milestone_type] : null;
      const bConfig = b.system_milestone_type ? SYSTEM_MILESTONE_CONFIG[b.system_milestone_type] : null;
      return (aConfig?.order || 0) - (bConfig?.order || 0);
    });

    // Sort user milestones by due date
    user.sort((a, b) => {
      if (!a.due_date && !b.due_date) return 0;
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
    });

    return { setupAwardMilestones: setupAward, userMilestones: user, completionMilestones: completion };
  }, [milestones]);

  // Calculate progress for each section
  const getProgress = (milestones: Milestone[]) => {
    const completed = milestones.filter(m => m.is_completed).length;
    return { completed, total: milestones.length };
  };

  const setupAwardProgress = getProgress(setupAwardMilestones);
  const userProgress = getProgress(userMilestones);
  const completionProgress = getProgress(completionMilestones);

  const getTypeColor = (type: string) => {
    const colors = {
      planning: 'bg-blue-500',
      design: 'bg-purple-500', 
      development: 'bg-green-500',
      review: 'bg-yellow-500',
      testing: 'bg-orange-500',
      delivery: 'bg-red-500',
      client_approval: 'bg-pink-500',
      installation: 'bg-indigo-500'
    };
    return colors[type as keyof typeof colors] || 'bg-gray-500';
  };

  // Render a single system milestone (compact style)
  const renderSystemMilestone = (milestone: Milestone) => {
    const config = milestone.system_milestone_type ? SYSTEM_MILESTONE_CONFIG[milestone.system_milestone_type] : null;
    
    return (
      <div key={milestone.id} className="flex items-center gap-3 py-2 px-3 rounded-md bg-muted/30">
        <div className={`flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center ${
          milestone.is_completed ? 'bg-green-500' : 'bg-muted-foreground/30'
        }`}>
          {milestone.is_completed ? (
            <CheckCircle className="h-4 w-4 text-white" />
          ) : (
            <Circle className="h-3 w-3 text-muted-foreground" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${milestone.is_completed ? 'text-foreground' : 'text-muted-foreground'}`}>
            {config?.label || milestone.milestone_name}
          </p>
        </div>
        {milestone.is_completed && milestone.completed_at && (
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {format(new Date(milestone.completed_at), 'MMM d, yyyy')}
          </span>
        )}
      </div>
    );
  };

  // Render a single user milestone (full card style)
  const renderUserMilestone = (milestone: Milestone, index: number) => {
    return (
      <div key={milestone.id} className="relative flex items-start space-x-4">
        {/* Timeline dot */}
        <div className={`relative z-10 flex h-10 w-10 items-center justify-center rounded-full border-4 border-background ${
          milestone.is_completed ? 'bg-green-500' : getTypeColor(milestone.milestone_type)
        }`}>
          {milestone.is_completed ? (
            <CheckCircle className="h-5 w-5 text-white" />
          ) : (
            <span className="text-xs font-bold text-white">{index + 1}</span>
          )}
        </div>

        {/* Milestone content */}
        <div className="flex-1 min-w-0 pb-6">
          <div 
            className={`bg-card border rounded-lg p-4 ${
              onMilestoneClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''
            } ${milestone.is_blank ? 'border-orange-200 bg-orange-50' : ''} ${
              milestone.is_completed ? 'border-green-200 bg-green-50 opacity-75' : ''
            }`}
            onClick={() => onMilestoneClick?.(milestone)}
          >
            <div className="flex items-start justify-between mb-2">
              <h3 className="font-semibold text-foreground">{milestone.milestone_name}</h3>
              <div className="flex items-center gap-2">
                {milestone.is_completed && (
                  <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
                    Completed
                  </Badge>
                )}
                {milestone.is_blank && !milestone.is_completed && (
                  <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-200">
                    Needs completion
                  </Badge>
                )}
                <Badge variant="secondary" className="text-xs">
                  {milestone.milestone_type.replace('_', ' ')}
                </Badge>
              </div>
            </div>

            <div className="space-y-2 text-sm">
              {milestone.due_date && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>Due: {format(new Date(milestone.due_date), 'PPP')}</span>
                </div>
              )}

              <div>
                <span className="font-medium text-foreground">Deliverable:</span>
                <span className="ml-2 text-muted-foreground">{milestone.deliverable}</span>
              </div>

              <div>
                <span className="font-medium text-foreground">Owner:</span>
                <Badge 
                  variant="outline" 
                  className={`ml-2 ${
                    milestone.action_owner === 'client' 
                      ? 'bg-blue-100 text-blue-800 border-blue-200' 
                      : 'bg-green-100 text-green-800 border-green-200'
                  }`}
                >
                  {milestone.action_owner}
                </Badge>
              </div>

              {milestone.description && (
                <p className="text-muted-foreground mt-2">{milestone.description}</p>
              )}

              {/* Action Buttons */}
              {(onMarkComplete || onRequestChange || onEditMilestone) && (
                <div className="mt-3 pt-3 border-t">
                  <div className="flex gap-2 flex-wrap">
                    {canEdit && onEditMilestone && !milestone.is_locked && !milestone.is_completed && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditMilestone(milestone);
                        }}
                      >
                        {milestone.is_blank ? 'Complete' : 'Edit'}
                      </Button>
                    )}

                    {canRequestChange && onRequestChange && isFullyApproved && !milestone.is_blank && !milestone.is_completed && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRequestChange(milestone);
                        }}
                      >
                        Request Change
                      </Button>
                    )}

                    {onMarkComplete && 
                     isFullyApproved &&
                     currentUserRole === milestone.action_owner && 
                     !milestone.is_completed && 
                     !milestone.is_blank && (
                      <Button 
                        size="sm" 
                        onClick={(e) => {
                          e.stopPropagation();
                          onMarkComplete(milestone);
                        }}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <CheckCircle className="mr-1 h-3 w-3" />
                        Mark Complete
                      </Button>
                    )}

                    {milestone.show_upload_button && 
                     isFullyApproved &&
                     !milestone.is_completed && 
                     currentUserRole === milestone.action_owner &&
                     jobPublicId && (
                      <Button
                        size="sm"
                        variant="default"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/jobs/${jobPublicId}?tab=documents&subtab=upload`);
                        }}
                      >
                        <Upload className="h-3 w-3 mr-1" />
                        Upload Files
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (milestones.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <p className="text-muted-foreground">No milestones in timeline yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Project Timeline</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* System Milestones (Setup & Award) */}
        {setupAwardMilestones.length > 0 && (
          <Collapsible open={setupAwardOpen} onOpenChange={setSetupAwardOpen}>
            <div className="rounded-lg border border-border bg-muted/20">
              <CollapsibleTrigger asChild>
                <button className="flex items-center justify-between w-full p-4 hover:bg-muted/30 transition-colors rounded-t-lg">
                  <div className="flex items-center gap-2">
                    <Lock className="h-4 w-4 text-muted-foreground" />
                    <span className="font-semibold text-foreground">System Milestones</span>
                    <Badge variant="secondary" className="text-xs">
                      Setup & Award
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      ({setupAwardProgress.completed}/{setupAwardProgress.total} complete)
                    </span>
                  </div>
                  {setupAwardOpen ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="px-4 pb-4 space-y-1">
                  <p className="text-xs text-muted-foreground mb-3">
                    Auto-tracked workflow stages • Read-only
                  </p>
                  {setupAwardMilestones.map(renderSystemMilestone)}
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        )}

        {/* User Milestones */}
        <div className="rounded-lg border border-primary/20 bg-card">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-primary" />
              <span className="font-semibold text-foreground">User Milestones</span>
              <span className="text-sm text-muted-foreground">
                ({userProgress.completed}/{userProgress.total} complete)
              </span>
            </div>
            {onAddMilestone && canEdit && (
              <Button size="sm" variant="outline" onClick={onAddMilestone}>
                + Add Milestone
              </Button>
            )}
          </div>
          <div className="p-4">
            {userMilestones.length > 0 ? (
              <>
                <p className="text-xs text-muted-foreground mb-4">
                  Milestones you create and manage
                </p>
                <div className="relative">
                  {/* Timeline line for user milestones */}
                  {userMilestones.length > 1 && (
                    <div className="absolute left-5 top-5 bottom-10 w-0.5 bg-primary/20" />
                  )}
                  <div className="space-y-0">
                    {userMilestones.map((milestone, index) => renderUserMilestone(milestone, index))}
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-6">
                <p className="text-muted-foreground text-sm">No user milestones yet.</p>
                {onAddMilestone && canEdit && (
                  <Button size="sm" variant="outline" onClick={onAddMilestone} className="mt-3">
                    + Add Your First Milestone
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* System Milestones (Completion) */}
        {completionMilestones.length > 0 && (
          <Collapsible open={completionOpen} onOpenChange={setCompletionOpen}>
            <div className="rounded-lg border border-border bg-muted/20">
              <CollapsibleTrigger asChild>
                <button className="flex items-center justify-between w-full p-4 hover:bg-muted/30 transition-colors rounded-t-lg">
                  <div className="flex items-center gap-2">
                    <Lock className="h-4 w-4 text-muted-foreground" />
                    <span className="font-semibold text-foreground">System Milestones</span>
                    <Badge variant="secondary" className="text-xs">
                      Completion
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      ({completionProgress.completed}/{completionProgress.total} complete)
                    </span>
                  </div>
                  {completionOpen ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="px-4 pb-4 space-y-1">
                  <p className="text-xs text-muted-foreground mb-3">
                    Auto-tracked workflow stages • Read-only
                  </p>
                  {completionMilestones.map(renderSystemMilestone)}
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
}
